/**
 * Descriptors for the "More ▾" menu, except the DBA group — that lives in
 * `toolbarDbaItems.ts`.
 *
 * Labels, tooltips, testids, trailing notes and gating are verbatim
 * carry-overs from the hand-written JSX this replaced;
 * `MainToolbar.moreMenuParity.test.tsx` pins the rendered result across four
 * connection/engine scenarios.
 *
 * Open SQL File's and Import Data's `disabled` gate keys off
 * `hasFallbackConnection` ("selected, else first connected") rather than the
 * tree selection — that is INJ-2's fix for the two dead rows in Jean-Luc's
 * screenshot, and narrowing it back would re-open the bug.
 */
import {
  BarChart3,
  BookOpen,
  FileSearch,
  FileText,
  FolderGit2,
  FolderInput,
  GitCompare,
  Link2,
  Lock,
  Map,
  Settings,
  Table,
  TableProperties,
  Upload,
  Workflow,
} from 'lucide-react';
import { tableExplorerUnsupportedReason } from './toolbarEngineFilters';
import type { ToolbarItemDescriptor, ToolbarModelDeps } from './toolbarItemTypes';

export function buildMoreToolbarItems({ gating, actions }: ToolbarModelDeps): ToolbarItemDescriptor[] {
  return [
    {
      // Global entry: the active Query tab's Visual view on the caret
      // statement, or a new Query tab in its Visual view when no Query tab is
      // in front. The Query Editor toolbar has its own SQL | Visual switch for
      // the per-tab case, so this lives in More ▾ rather than the primary row.
      id: 'visual-query',
      render: 'menu-item',
      label: 'Visual Query Editor',
      testId: 'visual-query-button',
      icon: Workflow,
      tooltip: 'Visual view of the active query tab (Cmd+Shift+V / Ctrl+Shift+V)',
      visible: gating.betaEnabled['visual-query-editor'],
      commandId: 'workspace:open-visual-query-editor',
      run: actions['visual-query'],
    },
    {
      id: 'new-notebook',
      render: 'menu-item',
      label: 'New Notebook',
      testId: 'new-notebook-menu',
      icon: BookOpen,
      visible: !gating.notebooksUnsupported,
      commandId: 'workspace:new-notebook',
      run: actions['new-notebook'],
    },
    {
      id: 'open-notebook',
      render: 'menu-item',
      label: 'Open Notebook',
      testId: 'open-notebook-menu',
      icon: FolderInput,
      commandId: 'workspace:open-notebook',
      run: actions['open-notebook'],
    },
    {
      id: 'open-sql-file',
      render: 'menu-item',
      label: 'Open SQL File',
      testId: 'open-query-file-menu-item',
      icon: FileText,
      disabled: !gating.hasFallbackConnection,
      commandId: 'workspace:open-sql-file',
      run: actions['open-sql-file'],
    },
    {
      id: 'open-execution-plan',
      render: 'menu-item',
      label: 'Open Execution Plan',
      testId: 'open-plan-menu-item',
      icon: FileSearch,
      disabled: gating.browserMode,
      note: gating.browserMode ? 'Requires desktop app' : undefined,
      commandId: 'workspace:open-execution-plan',
      run: actions['open-execution-plan'],
    },
    {
      id: 'deadlock-analyzer',
      render: 'menu-item',
      label: 'Deadlock Analyzer',
      testId: 'deadlock-analyzer-menu-item',
      icon: Lock,
      commandId: 'workspace:deadlock-analyzer',
      run: actions['deadlock-analyzer'],
    },
    {
      id: 'table-designer',
      render: 'menu-item',
      label: 'Designer',
      testId: 'table-designer-menu-item',
      icon: Settings,
      commandId: 'workspace:table-designer',
      run: actions['table-designer'],
    },
    {
      id: 'table-explorer-more',
      render: 'menu-item',
      label: 'Explorer',
      testId: 'table-explorer-more-menu-item',
      icon: Table,
      disabled: gating.tableExplorerUnsupported,
      tooltip: gating.tableExplorerUnsupported
        ? tableExplorerUnsupportedReason(gating.selectedConnectionEngine)
        : undefined,
      note: gating.tableExplorerUnsupported ? 'Not supported' : undefined,
      commandId: 'workspace:table-explorer',
      run: actions['table-explorer-more'],
    },
    {
      id: 'data-profile',
      render: 'menu-item',
      label: 'Profile',
      testId: 'data-profile-more-menu-item',
      icon: BarChart3,
      commandId: null, // no palette/menu-bar equivalent today
      run: actions['data-profile'],
    },
    {
      id: 'meta-info-manager',
      render: 'menu-item',
      label: 'Database Meta Info',
      testId: 'meta-info-manager-menu-item',
      icon: Link2,
      commandId: null, // no palette/menu-bar equivalent today
      run: actions['meta-info-manager'],
    },
    {
      id: 'spatial-explorer',
      render: 'menu-item',
      label: 'Spatial Explorer',
      testId: 'spatial-explorer-menu-item',
      icon: Map,
      disabled: gating.spatialExplorerUnsupported,
      tooltip: gating.spatialExplorerUnsupported
        ? "Spatial Explorer isn't supported for this connection's engine"
        : undefined,
      note: gating.spatialExplorerUnsupported ? 'Not supported' : undefined,
      commandId: 'workspace:spatial-explorer',
      run: actions['spatial-explorer'],
    },
    {
      id: 'schema-compare',
      render: 'menu-item',
      label: 'Schema Compare',
      testId: 'schema-compare-menu-item',
      icon: GitCompare,
      commandId: 'workspace:schema-compare',
      run: actions['schema-compare'],
    },
    {
      id: 'data-compare',
      render: 'menu-item',
      label: 'Data Compare',
      testId: 'data-compare-menu-item',
      icon: TableProperties,
      commandId: 'workspace:data-compare',
      run: actions['data-compare'],
    },
    {
      id: 'import-data',
      render: 'menu-item',
      label: 'Import Data...',
      testId: 'import-data-menu-item',
      icon: Upload,
      disabled: !gating.hasFallbackConnection || gating.importDataUnsupportedReason !== undefined,
      tooltip: gating.importDataUnsupportedReason
        ?? (!gating.hasFallbackConnection ? 'Connect to a database to import data' : undefined),
      note: gating.importDataUnsupportedReason ? 'Not supported' : undefined,
      visible: gating.betaEnabled['data-import'],
      commandId: 'workspace:import-data',
      run: actions['import-data'],
    },
    {
      id: 'database-blueprint',
      render: 'menu-item',
      label: 'Create Database Blueprint...',
      testId: 'database-blueprint-menu-item',
      icon: FolderGit2,
      visible: gating.betaEnabled['database-blueprint'],
      commandId: 'workspace:database-blueprint',
      run: actions['database-blueprint'],
    },
  ];
}
